// UPPDRAG-26 — pure helpers for the move-booking RPC call. Extracted so
// vitest can guard against regressions in the kronor↔öre conversion that
// caused the entire drag-drop move flow to silently fail in prod.
//
// Background: the move_booking Postgres RPC was migrated to take prices
// in minor units (öre) but the frontend kept passing kronor under the
// old `_new_price_total` / `_new_price_per_hour` parameter names. PostgREST
// returned a `Could not find function` 404 → admins saw "Kunde inte
// flytta" with no diagnostic. This helper fixes the conversion at the
// boundary so callers can stay in kronor.

/**
 * Converts kronor (SEK as a `number` with optional decimals) to minor
 * units (öre, integer). Returns `undefined` for null/undefined input so
 * the caller can omit the parameter from the RPC payload entirely
 * (matches the RPC's `DEFAULT NULL` fallback semantics).
 *
 * Rounds to the nearest öre — the legacy `price_total` column carried
 * floats with rounding error. Math.round is the same approach used by
 * the dual_write_bookings_minor trigger and the booking_moves audit
 * insert.
 */
export function kronorToMinor(value: number | null | undefined): number | undefined {
  if (value == null) return undefined;
  return Math.round(value * 100);
}

// UPPDRAG-29 — dirty-detection for MoveBookingDialog. The dialog used to
// pre-fill its "TILL" (target) form fields with the original booking
// values, then disable the submit button when nothing differed from the
// original. Since the values were always equal at mount, the button was
// permanently greyed out until the admin manually changed something —
// which most admins didn't realise they had to do, leading to a "stuck"
// dialog (Rickard reported this in prod).
//
// Fix (Option 2): initialize the form fields as empty strings and treat
// empty as "use the original". The helper below resolves "effective"
// values for downstream computation (price calc, conflict check,
// availability) while exposing a `hasChanged` flag that is true ONLY
// when at least one field has an explicit non-empty choice that differs
// from the original.
//
// This means: open dialog → all 3 fields empty + originals shown as
// placeholders → button disabled (correct: no actual change yet) → admin
// picks a new court / date / time → button enables. The admin cannot
// accidentally "move" a booking to its current slot.
export interface MoveBookingFormChoice {
  newCourtId: string;
  newDate: string;
  newTime: string;
}

export interface MoveBookingOriginal {
  originalCourtId: string;
  originalDate: string;
  originalTime: string;
}

export interface MoveBookingEffective {
  effectiveCourtId: string;
  effectiveDate: string;
  effectiveTime: string;
  hasChanged: boolean;
}

/**
 * Resolves form choices vs originals for the move-booking dialog.
 *
 * Empty string in a choice field → "use the original". `hasChanged` is
 * true only when at least one choice is non-empty AND differs from the
 * corresponding original (so explicitly re-selecting the same value
 * doesn't enable submit).
 */
export function resolveMoveBookingValues(
  choice: MoveBookingFormChoice,
  original: MoveBookingOriginal,
): MoveBookingEffective {
  const effectiveCourtId = choice.newCourtId || original.originalCourtId;
  const effectiveDate = choice.newDate || original.originalDate;
  const effectiveTime = choice.newTime || original.originalTime;
  const hasChanged =
    (choice.newCourtId !== "" && choice.newCourtId !== original.originalCourtId) ||
    (choice.newDate !== "" && choice.newDate !== original.originalDate) ||
    (choice.newTime !== "" && choice.newTime !== original.originalTime);
  return { effectiveCourtId, effectiveDate, effectiveTime, hasChanged };
}
